import { useState, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { Download, Paperclip } from "lucide-react";
import { Loader } from "@/components/shared/loader";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { submissionService } from "@/features/submissions/services/submissionService";
import { submissionTypeLabel } from "@/features/submissions/lib/submission-display";
import { handleApiError } from "@/lib/error-handler";
import { formatBytes, formatOptionalArabicDateTime } from "@/lib/formatters";
import { queryKeys } from "@/lib/query-keys";
import type { Submission } from "@/types/submission";

function Field({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-4 border-b border-border py-2 last:border-0">
      <span className="shrink-0 text-xs text-muted-foreground">{label}</span>
      <span className="whitespace-pre-wrap break-words text-right text-sm font-medium">
        {value}
      </span>
    </div>
  );
}

interface SubmissionDetailSheetProps {
  submissionId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The row already loaded by the list — drawn instantly while the detail request is in flight. */
  preview?: Submission;
}

export function SubmissionDetailSheet({
  submissionId,
  open,
  onOpenChange,
  preview,
}: SubmissionDetailSheetProps) {
  const [isDownloading, setIsDownloading] = useState(false);

  const { data, isLoading, error } = useQuery({
    queryKey: queryKeys.submissions.detail(submissionId ?? ""),
    queryFn: () => submissionService.get(submissionId!),
    enabled: open && Boolean(submissionId),
  });

  const submission = data ?? preview;
  const attachment = submission?.attachment;

  async function onDownload() {
    if (!submission || !attachment) return;
    setIsDownloading(true);
    try {
      await submissionService.downloadAttachment(
        submission.id,
        attachment.originalName,
      );
    } catch (err) {
      handleApiError(err, { customMessage: "تعذّر تنزيل المرفق" });
    } finally {
      setIsDownloading(false);
    }
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="left" className="w-full overflow-y-auto sm:max-w-lg">
        <SheetHeader className="text-right">
          <SheetTitle>تفاصيل الطلب</SheetTitle>
          <SheetDescription className="text-right">
            {submission ? submissionTypeLabel(submission.type) : "—"}
          </SheetDescription>
        </SheetHeader>

        <div className="mt-6 space-y-4">
          {isLoading && !submission && (
            <div className="flex justify-center py-8">
              <Loader />
            </div>
          )}
          {error && (
            <p className="text-right text-sm text-destructive">
              تعذر تحميل تفاصيل الطلب.
            </p>
          )}

          {submission && (
            <>
              <div className="rounded-lg border border-border p-3">
                <Field label="النوع" value={submissionTypeLabel(submission.type)} />
                <Field label="الاسم" value={submission.name ?? "—"} />
                <Field label="البريد الإلكتروني" value={submission.email ?? "—"} />
                <Field label="الهاتف" value={submission.phone ?? "—"} />
                <Field label="الموضوع" value={submission.subject ?? "—"} />
                <Field
                  label="الدائرة"
                  value={
                    submission.department
                      ? (submission.department.nameAr ?? submission.department.name)
                      : "—"
                  }
                />
                <Field
                  label="تاريخ الإرسال"
                  value={formatOptionalArabicDateTime(submission.createdAt)}
                />
              </div>

              <div className="space-y-1">
                <p className="text-xs font-medium text-muted-foreground">الرسالة</p>
                <div className="whitespace-pre-wrap break-words rounded-md bg-muted p-3 text-sm">
                  {submission.message || "—"}
                </div>
              </div>

              {/* CR-001 FR-1.8 — file name, size and the Download action («تنزيل», §2.2). */}
              <div className="space-y-2">
                <p className="text-xs font-medium text-muted-foreground">المرفق</p>
                {attachment ? (
                  <div className="rounded-lg border border-border p-3">
                    <div className="flex items-start gap-3">
                      <Paperclip
                        className="mt-0.5 size-4 shrink-0 text-muted-foreground"
                        aria-hidden="true"
                      />
                      <div className="min-w-0 flex-1 text-right">
                        <p
                          className="break-all text-sm font-medium"
                          dir="auto"
                          data-testid="attachment-name"
                        >
                          {attachment.originalName}
                        </p>
                        <p
                          className="text-xs text-muted-foreground"
                          data-testid="attachment-size"
                        >
                          {formatBytes(attachment.size)}
                        </p>
                      </div>
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="mt-3 w-full"
                      onClick={onDownload}
                      disabled={isDownloading}
                    >
                      <Download className="ml-2 size-4" aria-hidden="true" />
                      {isDownloading ? "جاري التنزيل..." : "تنزيل"}
                    </Button>
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">لا يوجد مرفق</p>
                )}
              </div>
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
